import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';
import { selectUser } from '../../../store/features/system';
import { API } from '../../../vendor/api';
import { alphanumericRegExp, emailRegExp, unsafePatternRegExp, urlPatternRegExp, urlProtocolPatternRegExp, } from '../constants';
const DEBOUNCED_DELAY = 500;
const expression = {
    unsafePattern: unsafePatternRegExp,
    alphanumeric: alphanumericRegExp,
    urlProtocolPattern: urlProtocolPatternRegExp,
    urlPattern: urlPatternRegExp,
    email: emailRegExp,
};
export const useValidation = () => {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { formatMessage } = useIntl();
    const { id: userId } = useSelector(selectUser);
    const debouncedUniqueEmailForSendInviteOption = useDebouncedCallback(async (getValues, fieldName, institutionId) => {
        const value = getValues(fieldName);
        if (value.length > 0) {
            const response = await API.institution.uniqueEmailForInstitution({
                email: value,
                isPending: true,
                institutionId,
            });
            if (response.existsInInstitution) {
                return formatMessage({
                    id: 'form.errors.notUniqueEmailInInstitution',
                });
            }
            if (response.existsInPendingList) {
                return formatMessage({
                    id: 'form.errors.uniqueEmailForPendingUsers',
                });
            }
        }
        return true;
    }, DEBOUNCED_DELAY);
    const debouncedUniqueEmailForCreateAccountOption = useDebouncedCallback(async (getValues, fieldName, institutionId) => {
        const value = getValues(fieldName);
        if (value.length > 0) {
            const response = await API.institution.uniqueEmailForInstitution({
                email: value,
                institutionId,
            });
            if (response.existsInInstitution) {
                return formatMessage({
                    id: 'form.errors.notUniqueEmailInInstitution',
                });
            }
            if (response.exists) {
                return formatMessage({
                    id: 'form.errors.uniqueEmailForAddUsersModal',
                });
            }
        }
        return true;
    }, DEBOUNCED_DELAY);
    return {
        required: (value) => Boolean(value.trim()) || formatMessage({ id: 'form.errors.required' }),
        safe: (value) => !expression.unsafePattern.test(value)
            ? true
            : formatMessage({ id: 'form.errors.safe' }),
        npi: (value) => value
            ? (value.match(/^\d+$/u) && value.length === 10) ||
                formatMessage({ id: 'form.errors.npi' })
            : true,
        alphanumeric: (value) => value
            ? expression.alphanumeric.test(value) ||
                formatMessage({ id: 'form.errors.alphanumeric' })
            : true,
        domain: (value) => value
            ? expression.alphanumeric.test(value) ||
                formatMessage({ id: 'form.errors.domain' })
            : true,
        url: (value) => {
            if (value) {
                const matchurlProtocolPattern = expression.urlProtocolPattern.test(value) ||
                    formatMessage({ id: 'form.errors.urlProtocol' });
                if (matchurlProtocolPattern === true) {
                    return (expression.urlPattern.test(value) ||
                        formatMessage({ id: 'form.errors.url' }));
                }
                return matchurlProtocolPattern;
            }
            return true;
        },
        validPassword: async (value) => {
            const { isValid } = await API.user.isValidPassword({
                id: userId,
                payload: { password: value },
            });
            return isValid || formatMessage({ id: 'form.errors.wrongPassword' });
        },
        passwordMatch: (value, passwordToCompare) => value === passwordToCompare ||
            formatMessage({ id: 'form.errors.passwordMatch' }),
        uniqueEmail: async (value) => {
            if (!value) {
                return true;
            }
            const { unique } = await API.user.unique({ email: value });
            return unique || formatMessage({ id: 'form.errors.uniqueEmail' });
        },
        uniqueEmailForSendInviteOption: async (getValues, fieldName, institutionId) => {
            return debouncedUniqueEmailForSendInviteOption(getValues, fieldName, institutionId);
        },
        uniqueEmailForCreateAccountOption: async (getValues, fieldName, institutionId) => {
            return debouncedUniqueEmailForCreateAccountOption(getValues, fieldName, institutionId);
        },
        emailMatch: (value, emailToCompare) => value.toLowerCase() === emailToCompare.toLowerCase() ||
            formatMessage({ id: 'form.errors.emailMatch' }),
        email: (value) => expression.email.test(value.toLowerCase()) ||
            formatMessage({ id: 'form.errors.invalidEmail' }),
    };
};
